import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileTextIcon } from '@radix-ui/react-icons';
import { Select, TextInput } from '@/shared/ui';
import type { PropertyFilters } from './constants';
import {
  PAYMENT_FREQUENCY_OPTIONS,
  RENT_GUARANTEE_OPTIONS,
  RENT_INCLUDED_EXPENSES_OPTIONS,
  ADMINISTRATION_TYPE_OPTIONS,
} from './constants';
import { SectionHeader, FieldLabel, ChipBtn } from './parts';

interface Props {
  filters: PropertyFilters;
  setFilters: (f: PropertyFilters) => void;
  toggleInArray: (field: keyof PropertyFilters, value: string) => void;
}

/**
 * Checks whether an array-valued filter field already includes a value.
 */
const has = (filters: PropertyFilters, field: keyof PropertyFilters, value: string) =>
  ((filters[field] as string[] | undefined) || []).includes(value);

export const FilterSectionRental: React.FC<Props> = ({ filters, setFilters, toggleInArray }) => {
  const { t } = useTranslation('features/properties');

  const frequencyData = PAYMENT_FREQUENCY_OPTIONS.map((token) => ({
    value: token,
    label: t(`form.options.rentalPaymentFrequency.${token}`, { defaultValue: token }),
  }));

  const adminData = ADMINISTRATION_TYPE_OPTIONS.map((token) => ({
    value: token,
    label: t(`form.options.administrationType.${token}`, { defaultValue: token }),
  }));

  return (
    <div>
      <SectionHeader icon={<FileTextIcon width={14} height={14} />} title={t('filterPanel.rental')} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div>
          <FieldLabel>{t('filterPanel.rentPaymentFrequency')}</FieldLabel>
          <Select
            data={frequencyData}
            value={filters.rentPaymentFrequency || null}
            onChange={(v: string | string[] | null) => setFilters({ ...filters, rentPaymentFrequency: Array.isArray(v) ? v[0] : (v ?? '') })}
            placeholder={t('filterPanel.selectFrequency')}
          />
        </div>
        <div>
          <FieldLabel>{t('filterPanel.rentGuarantee')}</FieldLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
            {RENT_GUARANTEE_OPTIONS.map((token) => (
              <ChipBtn
                key={token}
                value={t(`form.options.rentGuarantee.${token}`, { defaultValue: token })}
                active={has(filters, 'rentGuarantee', token)}
                onClick={() => toggleInArray('rentGuarantee', token)}
              />
            ))}
          </div>
        </div>
        <div>
          <FieldLabel>{t('filterPanel.rentIncludedExpenses')}</FieldLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
            {RENT_INCLUDED_EXPENSES_OPTIONS.map((token) => (
              <ChipBtn
                key={token}
                value={t(`form.options.rentalIncludedExpenses.${token}`, { defaultValue: token })}
                active={has(filters, 'rentIncludedExpenses', token)}
                onClick={() => toggleInArray('rentIncludedExpenses', token)}
              />
            ))}
          </div>
        </div>
        <div>
          <FieldLabel>{t('filterPanel.leaseTermMonths')}</FieldLabel>
          <TextInput
            placeholder="12"
            value={filters.leaseTermMonths || ''}
            onChange={(e) => setFilters({ ...filters, leaseTermMonths: e.target.value })}
          />
        </div>
        <div>
          <FieldLabel>{t('filterPanel.administrationType')}</FieldLabel>
          <Select
            data={adminData}
            value={filters.administrationType || null}
            onChange={(v: string | string[] | null) => setFilters({ ...filters, administrationType: Array.isArray(v) ? v[0] : (v ?? '') })}
            placeholder={t('filterPanel.selectOption')}
          />
        </div>
      </div>
    </div>
  );
};
